import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { buildBusinessPriorityUidToNameMap } from 'partner360/features/mdfActivities/helpers/activityBusinessPriorities'
import { IMdfActivityWithBusinessPriorities } from 'partner360/features/mdfActivities/model/types'

import type {
  CockpitFiscalQuarter,
  CockpitPeriodKey,
  ICockpitKeyInitiative,
  ICockpitMdfActivity,
  ICockpitPlanningPeriod,
} from '../../model/types'

import { getSelectedCockpitQuarters, OVERALL_OFFERING } from './getCockpitInitiativesInScope'

/** Quarter an activity belongs to, read from its `Timeframe` (`"26Q1"`). `null` when the
 *  timeframe names no quarter — such an activity is never filtered out by a quarter selection,
 *  since there is nothing to compare against. */
export const getActivityQuarter = (timeframe: string): CockpitFiscalQuarter | null => {
  const match = /Q([1-4])/i.exec(timeframe)

  return match ? (Number(match[1]) as CockpitFiscalQuarter) : null
}

interface IParams {
  activities: ICockpitMdfActivity[]
  initiatives: ICockpitKeyInitiative[]
  kiBusinessPriorities: IKIBusinessPriority[]
  selectedOffering: string
  selectedPeriods: CockpitPeriodKey[]
  planningPeriod: ICockpitPlanningPeriod | null
}

interface IFilterParams {
  activities: ICockpitMdfActivity[]
  selectedOffering: string
  selectedPeriods: CockpitPeriodKey[]
  planningPeriod: ICockpitPlanningPeriod | null
}

/**
 * The MDF activities the dashboard's offering and period select, without the business-priority
 * enrichment. Shared so the collapsed tile's MDF totals cover exactly the activities the
 * expanded MDF sub-tab lists.
 */
export const filterCockpitMdfActivities = ({
  activities,
  selectedOffering,
  selectedPeriods,
  planningPeriod,
}: IFilterParams): ICockpitMdfActivity[] => {
  const selectedQuarters = getSelectedCockpitQuarters(selectedPeriods, planningPeriod?.fQuarter)

  return activities.filter((activity) => {
    const matchesOffering =
      selectedOffering === OVERALL_OFFERING || activity.programTypes.includes(selectedOffering)

    if (!matchesOffering) {
      return false
    }

    if (selectedQuarters === null) {
      return true
    }

    const quarter = getActivityQuarter(activity.timeframe)

    return quarter === null || selectedQuarters.includes(quarter)
  })
}

/**
 * The MDF activities the tile is showing, enriched with the GTM business priorities of their
 * linked key initiative — the same enrichment the MDF dashboard does, but reading the cockpit
 * payload's own initiatives instead of a separate query.
 *
 * Scoped by the dashboard's offering and period. The offering rides on the activity
 * (`programType`) because the cockpit fetches every offering at once, unlike the MDF widget,
 * whose stored procedure is asked for one offering and answers with a list already scoped.
 * An activity lists every offering it belongs to, so one spread across business units of two
 * offerings shows under both. The mapper derives this list from MDF business units when the
 * cockpit payload omits `ProgramTypes`. An activity whose offering cannot be established stays
 * visible under Overall only. The payload is already limited to the requested fiscal year, so
 * the year needs no filter of its own.
 */
export const getCockpitMdfActivitiesInScope = ({
  activities,
  initiatives,
  kiBusinessPriorities,
  selectedOffering,
  selectedPeriods,
  planningPeriod,
}: IParams): IMdfActivityWithBusinessPriorities[] => {
  const uidToName = buildBusinessPriorityUidToNameMap(kiBusinessPriorities)
  const initiativesById = new Map(initiatives.map((initiative) => [initiative.EntryId, initiative]))

  const getBusinessPriorities = (keyInitiativeId: number | null): string[] => {
    const priorities =
      keyInitiativeId === null
        ? undefined
        : initiativesById.get(keyInitiativeId)?.BusinessPriorities

    if (!priorities) {
      return []
    }

    return priorities
      .split(',')
      .map((uid) => uid.trim().toLocaleLowerCase())
      .filter(Boolean)
      .map((uid) => uidToName.get(uid))
      .filter((name): name is string => Boolean(name))
  }

  return filterCockpitMdfActivities({
    activities,
    selectedOffering,
    selectedPeriods,
    planningPeriod,
  }).map((activity) => ({
    ...activity,
    businessPriorities: getBusinessPriorities(activity.keyInitiativeId),
  }))
}
